<template>
  <div class="background-visuals title-visuals">
    <div ref="guidanceVisual" class="visual guidance-visual show">
      <p>Scroll down<span class="session-wide-only"> or tap the arrow keys</span></p>
    </div>
    <div ref="titleBarVisual" class="visual show title-bar-visual">
      <div
        ref="textWhiteOverlay"
        class="text-white-overlay session-wide-only"
        style="opacity: 0"
      ></div>
    </div>
    <div ref="titleVisual" class="visual show title-visual" data-test="session-title">
      <template v-if="sessionTitle">{{ sessionTitle }}</template>
      <template v-if="sessionNumber == 1"
        >Mapping your<br class="session-inline-only" />
        Strengths</template
      >
      <template v-if="sessionNumber == 2"
        >Your Solo<br class="session-inline-only" />
        Report</template
      >
      <template v-if="sessionNumber == 3">Others' views</template>
      <template v-if="sessionNumber == 4"
        >Your Viewpoints<br class="session-inline-only" />
        Report</template
      >
      <template v-if="sessionNumber == 5">Engage</template>
    </div>
    <div ref="initialLogoVisual" class="visual show initial-logo-visual">
      <img
        ref="initialSessionLogoImage"
        :src="sessionLogoImageSrc"
        alt="Session Logo"
        v-if="sessionLogoImageSrc"
      />
      <img
        ref="initialSessionLogoImage"
        src="../../assets/sessions/session-1/session-1-logo.png"
        alt="Session 1 Logo"
        class="session-1-logo"
        v-if="sessionNumber == 1"
      />
      <img
        ref="initialSessionLogoImage"
        src="../../assets/sessions/session-2-logo.png"
        alt="Session 2 Logo"
        class="session-2-logo"
        v-if="sessionNumber == 2"
      />
      <img
        ref="initialSessionLogoImage"
        src="../../assets/sessions/session-3/session-3-logo.png"
        alt="Session 3 Logo"
        class="session-3-logo"
        v-if="sessionNumber == 3"
      />
      <img
        ref="initialSessionLogoImage"
        src="../../assets/sessions/session-4-logo.png"
        alt="Session 4 Logo"
        class="session-4-logo"
        v-if="sessionNumber == 4"
      />
      <img
        ref="initialSessionLogoImage"
        src="../../assets/sessions/session-5/session-5-logo.png"
        alt="Session 5 Logo"
        class="session-5-logo"
        v-if="sessionNumber == 5"
      />

      <img
        ref="initialLogoImage"
        src="../../assets/brand/gyre-icon-sm-rounded.png"
        alt="Gyre Logo"
        class="session-jyre-logo"
        style="opacity: 0"
      />
    </div>
    <div
      ref="initialLogoForPositioningVisual"
      class="visual side-visual initial-logo-for-positioning"
    >
      <img
        ref="initialLogoForPositioningImage"
        src="../../assets/sessions/session-2-logo.png"
        alt="Gyre circles logo"
        style="margin: 0 auto; width: 50%"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { TimelineLite, TimelineMax } from "gsap";
import { getCurrentInstance, onMounted, ref } from "vue";

const instance = getCurrentInstance()?.proxy;
const session = instance?.$parent;
const titleTimescale = 1;

const props = defineProps<{
  sessionTitle: string;
  sessionLogoImageSrc: string | null;
  sessionNumber?: number;
}>();

const logoRect = ref<DOMRect | null>(null);

const initialLogoForPositioningImage = ref<Element | null>(null);
const initialLogoImage = ref<Element | null>(null);
const initialSessionLogoImage = ref<Element | null>(null);
const guidanceVisual = ref<Element | null>(null);
const titleBarVisual = ref<Element | null>(null);
const textWhiteOverlay = ref<Element | null>(null);
const titleVisual = ref<Element | null>(null);
const initialLogoVisual = ref<Element | null>(null);

onMounted(() => {
  logoRect.value = (initialLogoForPositioningImage.value as HTMLElement).getBoundingClientRect();
});

const sessionLogoIn = (tl: TimelineMax): TimelineLite =>
  tl
    .to(
      initialLogoImage.value!,
      titleTimescale,
      { autoAlpha: 0, ease: (session as any).titleTextEase },
      0,
    )
    .to(
      initialSessionLogoImage.value!,
      titleTimescale,
      { autoAlpha: 1, ease: (session as any).titleTextEase },
      0,
    );

const sessionLogoOut = (
  tl: TimelineMax,
  reduceSizeDuringFade = false,
): TimelineLite | undefined => {
  if (!initialSessionLogoImage.value) return;

  return tl.to(
    initialSessionLogoImage.value,
    titleTimescale,
    {
      autoAlpha: 0,
      scale: reduceSizeDuringFade ? 0.7 : 1,
      ease: (session as any).titleTextEase,
    },
    0,
  );
};

const leave = (tl: TimelineMax): void =>
  (session as any).zoomLeave(tl, [initialLogoImage.value!, initialSessionLogoImage.value!]);

const sessionLogoRotateOut = (tl: TimelineMax): TimelineLite =>
  tl.to(
    initialSessionLogoImage.value!,
    titleTimescale,
    { autoAlpha: 0, rotation: 180, ease: (session as any).titleTextEase },
    0,
  );

const rollIn = (tl: TimelineMax, switchToCsLogo = true): void => {
  logoRect.value = (initialLogoForPositioningImage.value as HTMLElement).getBoundingClientRect();

  tl.to(
    initialLogoVisual.value!,
    titleTimescale,
    {
      top: logoRect.value.top,
      left: logoRect.value.left,
      width: logoRect.value.width,
      height: logoRect.value.height,
      ease: (session as any).titleVisualEase,
    },
    0,
  )
    .to(
      initialLogoVisual.value!,
      titleTimescale,
      {
        css: { className: "+=transitioned" },
        autoCss: true,
        overwrite: "none",
        ease: (session as any).titleVisualEase,
      },
      0,
    )
    .to(
      titleBarVisual.value!,
      titleTimescale,
      { top: -200, bottom: -200, ease: (session as any).titleVisualEase },
      0,
    )
    .to(
      textWhiteOverlay.value!,
      titleTimescale,
      { autoAlpha: 1, ease: (session as any).titleVisualEase },
      0,
    )
    .to(
      titleVisual.value!,
      titleTimescale,
      { autoAlpha: 0, ease: (session as any).titleTextEase },
      0,
    );

  if (switchToCsLogo) {
    tl.to(
      initialLogoImage.value!,
      titleTimescale,
      { autoAlpha: 1, ease: (session as any).titleTextEase },
      0,
    ).to(
      initialSessionLogoImage.value!,
      titleTimescale,
      { autoAlpha: 0, ease: (session as any).titleTextEase },
      0,
    );
  }

  tl.to(
    guidanceVisual.value!,
    titleTimescale * 0.3,
    { autoAlpha: 0, marginTop: -70, ease: (session as any).titleVisualEase },
    0,
  );
};

function fadeOutOverlay(tl: TimelineMax): void {
  tl.to(titleBarVisual.value!, titleTimescale, { autoAlpha: 0 }, 0);
}

function fadeInOverlay(tl: TimelineMax, timescale: number = titleTimescale): void {
  tl.to(titleBarVisual.value!, timescale, { autoAlpha: 1 }, 0);
}

defineExpose({
  sessionLogoIn,
  sessionLogoOut,
  leave,
  sessionLogoRotateOut,
  rollIn,
  fadeOutOverlay,
  fadeInOverlay,
});
</script>
<!-- Add "scoped" attribute to limit CSS to this component only -->
<style lang="less" scoped>
.session-jyre-logo {
  background-color: white;
  border-radius: 50000px;
  padding: 10px;
}
</style>
